import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import {
    StyledDialog,
    StyledDialogContainer
} from '@/components/consent/StyledDialog';
import {
    OutlinedSecondaryButtonLG,
    PrimaryButtonLG
} from '@/components/shared/Button';
import { SMBold, SMRegular, SMSemiBold } from '@/typography/Typography';
import { useTranslation } from 'react-i18next';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { InputText } from '@/components/shared/InputText';
import { useErrorHandling } from '@/utils/errorHandling';

export interface IgnoreTransactionModalProps {
    selectedTransactionId: string;
    open: boolean;
    handleClose: () => void;
}

const IgnoreTransactionModal = ({
    selectedTransactionId,
    open,
    handleClose
}: IgnoreTransactionModalProps) => {
    const [message, setMessage] = useState('');

    const { t } = useTranslation();

    const { ignoreBankTransactionRecord } = usePaymentsMutations();

    const handleCancel = () => {
        setMessage('');
        handleClose?.();
    };
    const { backendErrorHandler } = useErrorHandling();

    useEffect(() => {
        if (ignoreBankTransactionRecord.isSuccess) {
            handleCancel();
        }
    }, [ignoreBankTransactionRecord.isSuccess]);

    useEffect(() => {
        if (ignoreBankTransactionRecord.error) {
            backendErrorHandler()(ignoreBankTransactionRecord.error);
        }
    }, [ignoreBankTransactionRecord.error]);

    return (
        <StyledDialog open={open} onClose={handleCancel}>
            <StyledDialogContainer
                style={{
                    width: '400px',
                    padding: '24px'
                }}
            >
                <Box m={1} mb={2} textAlign="center" width={'100%'}>
                    <SMSemiBold mb={1}>
                        {t('transaction_monitoring.ignore_transaction')}
                    </SMSemiBold>
                    <SMRegular>{selectedTransactionId}</SMRegular>
                </Box>

                <InputText
                    label="Message"
                    variant="outlined"
                    value={message}
                    multiline
                    rows={4}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Please add your details"
                />

                <SMRegular mt={2}>
                    Please take the time to add a relevant message when a
                    transaction's ignored.
                </SMRegular>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        mt: 3
                    }}
                    gap={1.5}
                >
                    <OutlinedSecondaryButtonLG
                        fullWidth
                        color="secondary"
                        style={{
                            height: '40px',
                            borderRadius: '8px'
                        }}
                        onClick={handleCancel}
                    >
                        <SMBold textTransform={'none'}>{t('Cancel')}</SMBold>
                    </OutlinedSecondaryButtonLG>

                    <PrimaryButtonLG
                        fullWidth
                        size="large"
                        style={{ height: '39px' }}
                        id={'ignore-transaction'}
                        onClick={() => {
                            ignoreBankTransactionRecord.mutate({
                                bankTransactionRecordId: selectedTransactionId,
                                comment: message
                            });
                        }}
                    >
                        <SMBold textTransform={'none'}>
                            {t(' Save and ignore')}
                        </SMBold>
                    </PrimaryButtonLG>
                </Box>
            </StyledDialogContainer>
        </StyledDialog>
    );
};

export default IgnoreTransactionModal;
